import { memo, useCallback, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Button, DrivePickerModal, Input, RichTextEditor } from '@aimsos/ui';

import { fetchDriveRoot, fetchFolderChildren } from '../../../../services/drives.js';
import { useDrives } from '../../hooks/useDrives.js';
import { useNoteForm } from '../../hooks/useNoteForm.js';
import NoteScopeControl from './NoteScopeControl.jsx';

import { FileText, Link2, Maximize2, Minimize2, Paperclip, Plus, X } from 'lucide-react';

/** Taller editor when expanded: the toolbar and the page stay put, the writing area grows. */
const EXPANDED_EDITOR_CLASS = '[&_.ProseMirror]:!min-h-[50vh]';

/**
 * The note form, shared by create and edit (CT-5528): title, body, scope, files
 * from the drive and URLs.
 *
 * `onSubmit(payload)` resolves to the saved id, or `null` when it failed. The
 * form never closes itself and never resets: the parent closes on success, so a
 * failed save leaves every value exactly as typed.
 *
 * Save is disabled until the title and the body say something, and a line next
 * to it states which one is missing — a blocked button always says why.
 *
 * `useTranslation` lives here, not in the parent: no pre-translated strings are
 * handed down.
 *
 * @param {object} props
 * @param {object} [props.initial] — values to start from (edit).
 * @param {'create'|'edit'} props.mode
 * @param {boolean} props.saving
 * @param {(payload: object) => Promise<string|null>} props.onSubmit
 * @param {() => void} props.onCancel
 */
const NoteForm = memo(function NoteForm({ initial, mode, saving, onSubmit, onCancel }) {
  const { t } = useTranslation('unified-profiles');
  const form = useNoteForm(initial);
  // Members, not `form.x` in the deps below: the compiler cannot preserve a
  // memoization whose dependency is a property of an object.
  const { setTitle, setBody, addFiles, removeFile, addUrl, removeUrl, toPayload } = form;
  const [expanded, setExpanded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [urlDraft, setUrlDraft] = useState('');
  const { drives, loading: drivesLoading } = useDrives({ enabled: pickerOpen });

  const handleTitle = useCallback((event) => setTitle(event.target.value), [setTitle]);
  const handleBody = useCallback((payload) => setBody(payload.html), [setBody]);
  const toggleExpanded = useCallback(() => setExpanded((value) => !value), []);

  const openPicker = useCallback(() => setPickerOpen(true), []);
  const closePicker = useCallback(() => setPickerOpen(false), []);
  const handlePicked = useCallback(
    (items) => {
      addFiles(items.map((item) => ({ id: item.document_uuid, name: item.name })));
      setPickerOpen(false);
    },
    [addFiles],
  );

  const handleUrlDraft = useCallback((event) => setUrlDraft(event.target.value), []);
  const commitUrl = useCallback(() => {
    if (addUrl(urlDraft)) setUrlDraft('');
  }, [addUrl, urlDraft]);
  const handleUrlKeyDown = useCallback(
    (event) => {
      if (event.key !== 'Enter') return;
      // Enter in the URL box adds the URL; it must not submit the note.
      event.preventDefault();
      commitUrl();
    },
    [commitUrl],
  );

  const canSave = form.isValid && !saving;
  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      if (!canSave) return;
      onSubmit(toPayload());
    },
    [canSave, onSubmit, toPayload],
  );

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      data-testid="note-form"
      data-mode={mode}
      className="flex min-h-0 flex-col gap-5"
    >
      <Input
        label={t('notes.form.title')}
        value={form.title}
        onChange={handleTitle}
        placeholder={t('notes.form.titlePlaceholder')}
        disabled={saving}
        required
        autoFocus
      />

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-content-secondary">{t('notes.form.body')}</span>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={expanded ? Minimize2 : Maximize2}
            onClick={toggleExpanded}
            aria-pressed={expanded}
            aria-label={expanded ? t('notes.form.collapse') : t('notes.form.expand')}
          />
        </div>
        <div data-testid="note-editor" data-expanded={expanded ? 'true' : 'false'} className={expanded ? EXPANDED_EDITOR_CLASS : ''}>
          <RichTextEditor
            value={form.body}
            inputFormat="html"
            onChange={handleBody}
            placeholder={t('notes.form.bodyPlaceholder')}
            disabled={saving}
          />
        </div>
      </div>

      <NoteScopeControl value={form.scope} onChange={form.setScope} disabled={saving} />

      <section aria-label={t('notes.form.attachments')} className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-content-secondary">{t('notes.form.attachments')}</span>
          <Button variant="secondary" size="sm" leftIcon={Paperclip} onClick={openPicker} disabled={saving}>
            {t('notes.form.attach')}
          </Button>
        </div>
        {form.files.length ? (
          <ul className="m-0 flex list-none flex-col gap-1 p-0" data-testid="note-form-files">
            {form.files.map((file) => (
              <li key={file.id} className="flex items-center justify-between gap-2 rounded-md border border-edge px-3 py-1.5 text-sm">
                <span className="flex min-w-0 items-center gap-2 text-content">
                  <FileText className="size-4 shrink-0 text-content-muted" aria-hidden />
                  <span className="truncate">{file.name}</span>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={X}
                  aria-label={t('notes.form.removeFile', { name: file.name })}
                  onClick={() => removeFile(file.id)}
                  disabled={saving}
                />
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      <section aria-label={t('notes.form.urls')} className="flex flex-col gap-2">
        <span className="text-sm font-medium text-content-secondary">{t('notes.form.urls')}</span>
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <Input
              aria-label={t('notes.form.urlInput')}
              placeholder={t('notes.form.urlPlaceholder')}
              value={urlDraft}
              onChange={handleUrlDraft}
              onKeyDown={handleUrlKeyDown}
              disabled={saving}
            />
          </div>
          <Button variant="secondary" size="md" leftIcon={Plus} onClick={commitUrl} disabled={saving || urlDraft.trim() === ''}>
            {t('notes.form.addUrl')}
          </Button>
        </div>
        {form.urls.length ? (
          <ul className="m-0 flex list-none flex-col gap-1 p-0" data-testid="note-form-urls">
            {form.urls.map((url) => (
              <li key={url} className="flex items-center justify-between gap-2 rounded-md border border-edge px-3 py-1.5 text-sm">
                <span className="flex min-w-0 items-center gap-2 text-content">
                  <Link2 className="size-4 shrink-0 text-content-muted" aria-hidden />
                  <span className="truncate">{url}</span>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={X}
                  aria-label={t('notes.form.removeUrl', { url })}
                  onClick={() => removeUrl(url)}
                  disabled={saving}
                />
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-edge pt-4">
        {form.invalidReason ? (
          <span id="note-form-reason" data-testid="note-form-reason" className="mr-auto text-xs text-content-muted">
            {t(form.invalidReason)}
          </span>
        ) : null}
        <Button variant="secondary" size="md" onClick={onCancel} disabled={saving}>
          {t('notes.form.cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="md"
          disabled={!canSave}
          aria-describedby={form.invalidReason ? 'note-form-reason' : undefined}
        >
          {t(mode === 'edit' ? 'notes.form.save' : 'notes.form.create')}
        </Button>
      </div>

      <DrivePickerModal
        isOpen={pickerOpen}
        onClose={closePicker}
        onConfirm={handlePicked}
        drives={drives}
        isLoadingDrives={drivesLoading}
        fetchDriveRoot={fetchDriveRoot}
        fetchFolderChildren={fetchFolderChildren}
        title={t('notes.form.pickerTitle')}
        cancelLabel={t('notes.form.cancel')}
        addSelectedLabel={t('notes.form.pickerAdd')}
        useBodyPortal
      />
    </form>
  );
});

export default NoteForm;
